import { Operation } from '@apollo/client';
import * as Sentry from '@sentry/browser';
import { OperationDefinitionNode, OperationTypeNode } from 'graphql';
import { isNil } from 'lodash';
import { beforeSend } from './beforeSend';
import { getCollectionViewScaleAggregate } from './collectionViewScale';
import { ignoreErrors } from './ignoreErrors';
import {
  LongTaskMonitor,
  getLongTaskDurationBucket,
  getLongTaskFineDurationBucket,
} from './longTaskMonitor';
import { getRoutePatternForPathname } from './longTaskRoute';
import {
  CollectionViewSentryContext,
  LongTaskWarningPayload,
  MBSentryBreadcrumb,
  MBSentryIdentifyPayload,
  MBSentryInitOptions,
} from './types';

const DEBUG_SENTRY = import.meta.env.VITE_DEBUG_SENTRY;

export const MBSentry = {
  longTaskMonitor: undefined as LongTaskMonitor | undefined,

  // Scale fields reported before the collection view's own effect has set the
  // context — child effects run first — buffered here and folded in on set.
  pendingCollectionViewUpdate: {} as Partial<CollectionViewSentryContext>,

  init({ dsn }: MBSentryInitOptions) {
    const trimmedDsn = dsn.trim() || undefined;
    const release = import.meta.env.VITE_VERSION;
    const environment = import.meta.env.MODE;
    const enabled = environment !== 'development' && !isNil(trimmedDsn);

    const context: Sentry.BrowserOptions = {
      dsn: trimmedDsn,
      release,
      environment,
      enabled,
      ignoreErrors,
      beforeSend,
    };

    if (DEBUG_SENTRY) {
      console.warn('[MBSentry] init', context);
    }

    this.longTaskMonitor?.stop();
    this.longTaskMonitor = undefined;
    Sentry.init(context);

    if (enabled) {
      this.longTaskMonitor = new LongTaskMonitor({
        breadcrumb: MBSentry.breadcrumb,
        captureLongTaskWarning: MBSentry.captureLongTaskWarning,
      });
      this.longTaskMonitor.start();
    }
  },

  identify({ id, name, businessId, businessName }: MBSentryIdentifyPayload) {
    if (DEBUG_SENTRY) {
      console.warn('[MBSentry] identify', { id, name, businessId });
    }
    Sentry.configureScope(scope => {
      scope.setUser({
        id: String(id),
        username: name,
      });
      scope.setTag('businessId', String(businessId));
      scope.setContext('business', {
        id: String(businessId),
        name: businessName,
      });
    });
  },

  setCollectionViewContext(context: CollectionViewSentryContext) {
    if (!this.longTaskMonitor) {
      return;
    }

    // Fold in whatever the view's data layer has already reported: buffered
    // updates from effects that ran before this one, and the current loaded
    // totals straight from the scale registry.
    const fullContext: CollectionViewSentryContext = {
      ...context,
      ...this.pendingCollectionViewUpdate,
      ...getCollectionViewScaleAggregate(),
    };

    this.longTaskMonitor.setCollectionViewContext(fullContext);

    if (DEBUG_SENTRY) {
      console.warn('[MBSentry] collectionViewContext', fullContext);
    }

    Sentry.configureScope(scope => {
      scope.setTag('collectionViewType', fullContext.viewType);
      scope.setTag('channelSlug', fullContext.channelSlug);
      scope.setContext('collectionView', collectionViewScopeContext(fullContext));
    });
  },

  /**
   * Merges scale fields (group/column counts, loaded totals) into the active
   * collection view context. Buffered when the context isn't set yet, so
   * reports from child effects survive until the view's own effect sets it.
   */
  updateCollectionViewContext(update: Partial<CollectionViewSentryContext>) {
    this.pendingCollectionViewUpdate = { ...this.pendingCollectionViewUpdate, ...update };

    const merged = this.longTaskMonitor?.updateCollectionViewContext(update);
    if (isNil(merged)) {
      return;
    }

    if (DEBUG_SENTRY) {
      console.warn('[MBSentry] updateCollectionViewContext', update);
    }

    Sentry.configureScope(scope => {
      scope.setContext('collectionView', collectionViewScopeContext(merged));
    });
  },

  /** Pushes the scale registry's current totals into the active context. */
  reportCollectionViewScale() {
    const aggregate = getCollectionViewScaleAggregate();
    this.updateCollectionViewContext(
      aggregate ?? { loadedEntityCount: undefined, totalEntityCount: undefined },
    );
  },

  clearCollectionViewContext() {
    this.pendingCollectionViewUpdate = {};

    if (!this.longTaskMonitor) {
      return;
    }

    this.longTaskMonitor.clearCollectionViewContext();

    if (DEBUG_SENTRY) {
      console.warn('[MBSentry] clearCollectionViewContext');
    }

    Sentry.configureScope(scope => {
      scope.setTag('collectionViewType', undefined);
      scope.setTag('channelSlug', undefined);
      scope.setContext('collectionView', null);
    });
  },

  captureLongTaskWarning({
    durationMs,
    startMs,
    pathname,
    windowTotalMs,
    collectionViewContext,
  }: LongTaskWarningPayload) {
    const durationBucket = getLongTaskDurationBucket(durationMs);
    if (isNil(durationBucket)) {
      return;
    }

    const fineDurationBucket = getLongTaskFineDurationBucket(durationMs);
    const route = getRoutePatternForPathname(pathname);

    const data = {
      durationMs,
      durationBucket,
      startMs,
      pathname,
      route,
      windowTotalMs,
      viewType: collectionViewContext?.viewType,
      channelSlug: collectionViewContext?.channelSlug,
      businessId: collectionViewContext?.businessId,
      businessName: collectionViewContext?.businessName,
      columnCount: collectionViewContext?.columnCount,
      groupCount: collectionViewContext?.groupCount,
      loadedEntityCount: collectionViewContext?.loadedEntityCount,
      totalEntityCount: collectionViewContext?.totalEntityCount,
    };

    if (DEBUG_SENTRY) {
      console.warn('[MBSentry] longTaskWarning', data);
    }

    MBSentry.breadcrumb({
      category: 'performance',
      message: 'longtask threshold exceeded',
      level: 'warning',
      data,
    });

    Sentry.withScope(scope => {
      scope.setLevel('warning');
      scope.setTag('longTaskDurationBucket', durationBucket);
      scope.setTag('longTaskRoute', route);
      if (!isNil(fineDurationBucket)) {
        scope.setTag('longTaskDurationMsBucket', fineDurationBucket);
      }
      if (!isNil(collectionViewContext)) {
        scope.setTag('collectionViewType', collectionViewContext.viewType);
        scope.setTag('channelSlug', collectionViewContext.channelSlug);
      }
      scope.setContext('longTask', data);
      // Fingerprint by bucket so Sentry splits reports by severity rather than
      // collapsing every long task into one issue.
      scope.setFingerprint(['main-thread-long-task', durationBucket]);
      Sentry.captureMessage(`Main thread long task threshold exceeded (${durationBucket})`);
    });
  },

  exception({
    error,
    data,
    fingerprint,
    tags,
  }: {
    error: Error;
    data?: Dict<any>;
    fingerprint?: string[];
    tags?: Dict<string>;
  }) {
    if (DEBUG_SENTRY) {
      console.warn('[MBSentry] exception', error, data);
    }
    MBSentry.breadcrumb({ data: data ?? {}, message: error.message, category: 'error' });
    if (isNil(fingerprint) && isNil(tags)) {
      Sentry.captureException(error);
      return;
    }
    Sentry.withScope(scope => {
      if (!isNil(fingerprint)) {
        scope.setFingerprint(fingerprint);
      }
      if (!isNil(tags)) {
        scope.setTags(tags);
      }
      if (!isNil(data)) {
        scope.setContext('data', data);
      }
      Sentry.captureException(error);
    });
  },

  // We log different types of events (analytics, redux, apollo) in breadcrumbs.
  // This makes it easy to understand what events led up to a crash.
  // https://docs.sentry.io/enriching-error-data/breadcrumbs/?platform=javascript
  breadcrumb({ level = 'info', ...payload }: MBSentryBreadcrumb) {
    if (DEBUG_SENTRY) {
      console.warn('[MBSentry]', payload.category, payload.message, payload.data);
    }
    Sentry.addBreadcrumb({ level, ...payload });
  },

  // TODO(fant): copy paste from index.native.ts
  breadcrumbFromApolloOperation(operation: Operation) {
    // https://github.com/graphql/graphql-js/blob/master/tstypes/language/ast.d.ts#L201
    const definition = operation.query.definitions[0] as OperationDefinitionNode;
    const type: OperationTypeNode = definition.operation;
    const name = operation.operationName;
    MBSentry.breadcrumb({
      message: `${type} ${name}`,
      category: 'apollo',
      data: operation.variables,
    });
  },

  message(text: string) {
    Sentry.captureMessage(text);
  },
};

function collectionViewScopeContext(context: CollectionViewSentryContext) {
  return {
    businessId: context.businessId,
    businessName: context.businessName,
    columnCount: context.columnCount,
    groupCount: context.groupCount,
    loadedEntityCount: context.loadedEntityCount,
    totalEntityCount: context.totalEntityCount,
  };
}
